import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import { mapToBase } from "go-core/api/searchSelectService";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { FormatAddressString } from "go-core/components/Formatters/FormatAddress";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import Tag from "../../../../../../../components/Common/Tag/Tag";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { api } from "../../../../../../../services/Api/api";
import { OrganizationApi, OrganizationTypeApi } from "../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../services/organizations/selectors";
import { selectUser } from "../../../../../../../services/users/selectors";
import OrganizationMergingModal from "../../../../../components/Admin/Organization/components/OrganizationMergingModal";
import OrganizationPartnersProvisionModal from "../../../../../components/Admin/Organization/components/OrganizationPartnersProvisionsModal";
import FormatSubscriptionLicenseType from "../../../../../components/FormatSubscriptionLicenseType/FormatSubscriptionLicenseType";
import OrganizationListAlerts from "../../../../../components/Organization/OrganizationListAlerts";
import OrganizationListApplicationsColumn from "../../../../../components/Organization/OrganizationListApplicationsColumn";
import OrganizationListPartnersInfo from "../../../../../components/Organization/OrganizationListPartnersInfo";
import { mapResponseToResponseDataWithId } from "../../../services/mapResponseToResponseDataWithId";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "ADMIN_ORGANIZATION";
const resourceType = "ORGANIZATION";

const List = ({ resource }: ListState) => {
	const [items, setItems] = useState<OrganizationApi[]>([]);
	const [showOrganizationMergingModal, setShowOrganizationMergingModal] = useState<boolean>(false);
	const [organizationToMerge, setOrganizationToMerge] = useState<OrganizationApi | undefined>(undefined);
	const [showPartnersProvisionsModal, setShowPartnersProvisionsModal] = useState<number | undefined>(undefined);
	const { addSuccessFlash } = useFlash();
	const { t } = useTranslation("admin");
	const organization = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const [params, setParams] = useState<Record<string, any>>({});
	const location = useLocation();
	const user = useSelector(selectUser);
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRemoveOrganization = async (item: OrganizationApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await adminApi.removeOrganization({ reason: "", password: "" }, item.id);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const onBlockLicense = async (item: OrganizationApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.blockSubscription(item.id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onUnblockLicense = async (item: OrganizationApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.unblockSubscription(item.id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const refresh = async () => {
		if (config.fetch) {
			await config.fetch(params).then((res) => {
				setItems(res);
			});
		}
	};
	const getBarBackgroundWidth = (percentValue: number) => {
		if (percentValue > 100) return "100%";
		return `${percentValue}%`;
	};
	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				render: (item: OrganizationApi) => {
					return (
						<div className="d-flex organization-name">
							<OrganizationListAlerts organization={item} />
							<div className="d-flex flex-column gap-1">
								<RenderLimitedText forceApplyMinContentHeight minWidth={255} offsetWidth={8}>
									<Link to={`/admin/organizations/${item.id}`}>{item.name}</Link>
								</RenderLimitedText>
								{item.tags.length > 0 && (
									<div className="d-flex flex-wrap">
										{item.tags.map((tag) => (
											<Tag key={tag}>{tag}</Tag>
										))}
									</div>
								)}
								<small>{item.company_id}</small>
								{item.stats && (
									<>
										<div className="d-flex align-items-center justify-content-between gap-2">
											<div className="organization-product-chart-bar">
												<div
													className="organization-product-chart-bar-background"
													style={{ width: getBarBackgroundWidth(item.stats.feature_percent) }}
												/>
											</div>
											{item.stats.feature_potential_count > 0 ? (
												<p className="mb-0 font-weight-bold percent-value">
													{`${item.stats.feature_percent}%`}{" "}
													<small>
														({item.stats.feature_count}/{item.stats.feature_potential_count}
														)
													</small>
												</p>
											) : (
												<p className="mb-0 font-weight-bold percent-value text-danger">Brak</p>
											)}
										</div>
									</>
								)}
							</div>
						</div>
					);
				},
				renderExport: (item: OrganizationApi) => {
					let result = item.name;
					if (item.tags.length > 0) result += ` | ${t("common.word.tags")}: ${item.tags.join(", ")}`;
					return result;
				},
			},
			{
				id: "contact_person",
				name: t("modules.organization.field.contact_person.title"),
				disableSorting: true,
				render: (item: OrganizationApi) => {
					return (
						<div className="d-flex flex-column">
							{item.contact_person && <span>{item.contact_person}</span>}
							{item.company_phone_number && (
								<small className="text-muted font-weight-bold">{item.company_phone_number}</small>
							)}
							{item.company_email && (
								<small className="text-muted font-weight-bold">{item.company_email}</small>
							)}
						</div>
					);
				},
			},
			{
				id: "company_name",
				name: t("common.word.company_name"),
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<div className={"d-flex flex-column"}>
						<span>{item.company_name}</span>
						<small className="text-muted font-weight-bold">
							{FormatAddressString({
								street: item.company_street,
								build_nr: item.company_build_nr,
								flat_nr: item.company_flat_nr,
								zip_code: item.company_zip_code,
								city: item.company_city,
							})}
						</small>
						{item.company_tax_id_no && (
							<small className="text-muted font-weight-bold">NIP: {item.company_tax_id_no}</small>
						)}
					</div>
				),
				renderExport: (item: OrganizationApi) => {
					return `${item.company_name ? item.company_name : ""} ${
						item.company_tax_id_no ? ` ${item.company_tax_id_no}` : ""
					}${item.company_tax_id_no || item.company_name ? ", " : ""} ${FormatAddressString({
						street: item.company_street,
						build_nr: item.company_build_nr,
						flat_nr: item.company_flat_nr,
						zip_code: item.company_zip_code,
						city: item.company_city,
					})}`;
				},
			},
			{
				id: "category",
				name: t("common.word.category"),
				type: "list",
				options: {
					CLUB: t("enums.organization_category.CLUB"),
					RESTAURANT: t("enums.organization_category.RESTAURANT"),
					CAFE: t("enums.organization_category.CAFE"),
					FOODTRUCK: t("enums.organization_category.FOODTRUCK"),
					PUB_BAR: t("enums.organization_category.PUB_BAR"),
					SUSHI: t("enums.organization_category.SUSHI"),
					SHOP: t("enums.organization_category.SHOP"),
					OTHERS: t("enums.organization_category.OTHERS"),
				},
				render: (data: OrganizationApi) => t(`enums.organization_category.${data.category}`),
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				render: (data: OrganizationApi) => <FormatResourceStatus status={data.status} />,
			},
			{
				id: "license_type",
				name: t("common.word.license_type"),
				render: (data: OrganizationApi) => {
					return (
						<>
							<FormatSubscriptionLicenseType license_type={data.license_type} />
							{data.license_expires_at && (
								<small>
									<br />
									{FormatDate(data.license_expires_at)}
								</small>
							)}
						</>
					);
				},
				renderExport: (data: OrganizationApi) => t(`enums.licenses.${data.license_type}`),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.created_at)?.split(",")[0],
			},
			{
				id: "cancelled_at",
				name: t("common.word.cancelled_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.cancelled_at),
			},
			{
				id: "activated_at",
				name: t("common.word.activated_at"),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.activated_at)?.split(",")[0],
			},
			{
				id: "last_used_at",
				name: t("common.word.last_used_at"),
				type: "date",
				render: (data: OrganizationApi) => FormatDate(data.last_used_at)?.split(",")[0],
			},
			{
				id: "application_type",
				name: t("modules.organization.field.application_type.title"),
				type: "list",
				options: {
					GOPOS: "GoPOS",
					GOCRM: "GoCRM",
					GOSTOCK: "GoStock",
				},
				render: (data: OrganizationApi) => <OrganizationListApplicationsColumn organization={data} />,
				renderExport: (data: OrganizationApi) => {
					return data.applications?.map((app: { name: string }) => app.name).join(", ");
				},
			},
			{
				id: "partners",
				name: t("common.word.partners"),
				render: (organization: OrganizationApi) => (
					<OrganizationListPartnersInfo
						organization={organization}
						setShowPartnersProvisionsModal={setShowPartnersProvisionsModal}
					/>
				),
				renderExport: (organization: OrganizationApi) =>
					organization.partners.map((partner) => partner.name).join(", "),
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getAdminPartnersListSearchSelect(search, params, options),
				},
			},
			{
				id: "partner_kam",
				name: t("enums.partner_types.KAM"),
				render: (organization: OrganizationApi) => {
					return (
						<RenderLimitedText minWidth={200}>
							{organization.partner_kam.map((partner, index) => {
								return (
									<>
										<Link to={`/partnership/${partner.partner_id}`} target={"_blank"}>
											{partner.name}
										</Link>
										{index < organization.partner_kam.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (organization: OrganizationApi) => organization.partner_kam.map((x) => x.name).join(", "),
			},
			{
				id: "partner_cs",
				name: t("enums.partner_types.CS"),
				render: (organization: OrganizationApi) => {
					return (
						<RenderLimitedText minWidth={200}>
							{organization.partner_cs.map((partner, index) => {
								return (
									<>
										<Link to={`/partnership/${partner.partner_id}`} target={"_blank"}>
											{partner.name}
										</Link>
										{index < organization.partner_cs.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (organization: OrganizationApi) => organization.partner_cs.map((x) => x.name).join(", "),
			},
			{
				id: "partner_top",
				name: t("enums.partner_types.TOP"),
				render: (organization: OrganizationApi) => {
					return (
						<RenderLimitedText minWidth={200}>
							{organization.partner_top.map((partner, index) => {
								return (
									<>
										<Link to={`/partnership/${partner.partner_id}`} target={"_blank"}>
											{partner.name}
										</Link>
										{index < organization.partner_top.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (organization: OrganizationApi) => organization.partner_top.map((x) => x.name).join(", "),
			},
			{
				id: "partner_sale",
				name: t("enums.partner_types.SALE"),
				render: (organization: OrganizationApi) => {
					return (
						<RenderLimitedText minWidth={200}>
							{organization.partner_sale.map((partner, index) => {
								return (
									<>
										<Link to={`/partnership/${partner.partner_id}`} target={"_blank"}>
											{partner.name}
										</Link>
										{index < organization.partner_sale.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (organization: OrganizationApi) =>
					organization.partner_sale.map((x) => x.name).join(", "),
			},
			{
				id: "partner_reseller",
				name: t("enums.partner_types.RESELLER"),
				render: (organization: OrganizationApi) => {
					return (
						<RenderLimitedText minWidth={200}>
							{organization.partner_reseller.map((partner, index) => {
								return (
									<>
										<Link to={`/partnership/${partner.partner_id}`} target={"_blank"}>
											{partner.name}
										</Link>
										{index < organization.partner_reseller.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (organization: OrganizationApi) =>
					organization.partner_reseller.map((x) => x.name).join(", "),
			},
			{
				id: "partner_affiliate",
				name: t("enums.partner_types.AFFILIATE"),
				render: (organization: OrganizationApi) => {
					return (
						<RenderLimitedText minWidth={200}>
							{organization.partner_affiliate.map((partner, index) => {
								return (
									<>
										<Link to={`/partnership/${partner.partner_id}`} target={"_blank"}>
											{partner.name}
										</Link>
										{index < organization.partner_affiliate.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (organization: OrganizationApi) =>
					organization.partner_affiliate.map((x) => x.name).join(", "),
			},
			{
				id: "mrr",
				name: t("common.word.mrr"),
				render: (item: OrganizationApi) => FormatMoney(item.mrr),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "mrr_sum",
				name: t("common.word.mrr_sum"),
				render: (item: OrganizationApi) => FormatMoney(item.mrr_sum),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "migrated_at",
				name: t("common.word.migrated_at"),
				render: (item: OrganizationApi) => FormatDate(item.migrated_at),
			},
			{
				id: "first_paid_at",
				name: t("common.word.first_paid_at"),
				render: (item: OrganizationApi) => FormatDate(item.first_paid_at),
			},
			{
				id: "payment_date_due",
				name: t("common.word.payment_date_due"),
				render: (item: OrganizationApi) => FormatDate(item.payment_date_due),
			},
			{
				id: "not_paid_invoices",
				name: t("modules.organization.field.not_paid_invoices.title"),
				render: (item: OrganizationApi) =>
					`${item.not_paid_invoices_count ?? 0} / ${FormatMoney(
						item.not_paid_invoices_amount ?? {
							amount: 0,
							currency: "PLN",
						}
					)}`,
			},
			{
				id: "last_sale_at",
				name: t("common.word.last_sale_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.last_sale_at),
			},
			{
				id: "first_sale_at",
				name: t("modules.organization.field.first_sale_at.title"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.first_sale_at),
			},
			{
				id: "last_activity_at",
				name: t("common.word.last_activity_at"),
				type: "date",
				render: (item: OrganizationApi) => FormatDate(item.last_activity_at),
			},
			{
				id: "first_activity_at",
				name: t("common.word.first_activity_at"),
				render: (item: OrganizationApi) => FormatDate(item.first_activity_at),
			},
			{
				id: "cancellation_reason",
				name: t("common.word.cancellation_reason_of_subscription"),
				type: "text",
				render: (item: OrganizationApi) => item.cancellation_reason,
			},
			{
				id: "cancellation_reason_details",
				name: t("modules.organization.field.cancellation_reason_details.title"),
				type: "text",
				render: (item: OrganizationApi) => item.cancellation_reason_details,
			},
			{
				id: "type",
				name: t("common.word.type", { ns: "lib" }),
				render: (item: OrganizationApi) => (item.type === "PROD" ? "Prod" : "Demo"),
			},
			{
				id: "potential_products",
				name: t("modules.organization.field.potential_products.title"),
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<RenderLimitedText minWidth={200}>
						{item.potential_products.map((product) => product).join(", ")}
					</RenderLimitedText>
				),
				renderExport: (item: OrganizationApi) => item.potential_products.map((product) => product).join(", "),
			},
			{
				id: "products",
				name: t("modules.organization.field.products.title"),
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<RenderLimitedText minWidth={200}>
						{item.products.map((product) => product).join(", ")}
					</RenderLimitedText>
				),
				renderExport: (item: OrganizationApi) => item.products.map((product) => product).join(", "),
			},
			{
				id: "potential_points_count",
				name: t("modules.organization.field.potential_points_count.title"),
				type: "number",
			},
			{
				id: "points_count",
				name: t("modules.organization.field.points_count.title"),
				type: "number",
			},
			{
				id: "potential_products_count",
				name: t("modules.organization.field.potential_products_count.title"),
				type: "number",
			},
			{
				id: "products_count",
				name: t("modules.organization.field.products_count.title"),
				type: "number",
			},
			{
				id: "products_percent",
				name: t("modules.organization.field.products_percent.title"),
				type: "number",
			},
			{
				id: "points_percent",
				name: t("modules.organization.field.points_percent.title"),
				type: "number",
			},
			{
				id: "abused_access",
				name: t("modules.organization.field.abused_access.title"),
				type: "number",
			},
			{
				id: "features_potential",
				name: "Features - potencjalne",
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<RenderLimitedText minWidth={200}>
						{item.features
							.filter((x) => x.potential)
							.map((feature) => feature.name)
							.join(", ")}
					</RenderLimitedText>
				),
				renderExport: (item: OrganizationApi) =>
					item.features
						.filter((x) => x.potential)
						.map((feature) => feature.name)
						.join(", "),
			},
			{
				id: "features_enabled",
				name: "Features - włączone",
				disableSorting: true,
				render: (item: OrganizationApi) => (
					<RenderLimitedText minWidth={200}>
						{item.features
							.filter((x) => x.enabled)
							.map((feature) => feature.name)
							.join(", ")}
					</RenderLimitedText>
				),
				renderExport: (item: OrganizationApi) =>
					item.features
						.filter((x) => x.enabled)
						.map((feature) => feature.name)
						.join(", "),
			},
			{
				id: "feature_potential_points",
				name: "Feature - ilość potencjalna punktów",
				type: "number",
				render: (item: OrganizationApi) => item.stats?.feature_potential_count,
			},
			{
				id: "feature_points",
				name: "Feature - ilość włączona punktów",
				type: "number",
				render: (item: OrganizationApi) => item.stats?.feature_points,
			},
			{
				id: "feature_potential_count",
				name: "Feature - ilość potencjalna",
				type: "number",
				render: (item: OrganizationApi) => item.stats?.feature_potential_count,
			},
			{
				id: "feature_count",
				name: "Feature - ilość włączona",
				type: "number",
				render: (item: OrganizationApi) => item.stats?.feature_count,
			},
			{
				id: "feature_percent",
				name: "Feature - %",
				type: "number",
				render: (item: OrganizationApi) => `${item.stats?.feature_percent}%`,
			},
			{
				id: "feature_points_percent",
				name: "Feature - % Punkty",
				type: "number",
				render: (item: OrganizationApi) => `${item.stats?.feature_points_percent}%`,
			},
		],
		filters: [
			{
				id: "partner",
				name: t("common.word.partner"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "partner_type",
				name: t("common.word.partner_type"),
				type: "list",
				options: {
					RESELLER: "Reseller",
					KAM: "KAM",
					TOP: "TOP",
					SALE: "Sale",
					CS: "CS",
					CSV: "CSv",
					SALE_PREMIUM: "Sale premium",
					AFFILIATE: "AFFILIATE",
				},
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
					BLOCKED: t("enums.common.status.BLOCKED", { ns: "translation" }),
					REMOVED: t("enums.common.status.REMOVED", { ns: "translation" }),
				},
			},
			{
				id: "license_type",
				name: t("common.word.license_type"),
				type: "list",
				options: {
					STANDARD: t("enums.licenses.STANDARD"),
					FUTURE: t("enums.licenses.FUTURE"),
					CANCELLED: t("enums.licenses.CANCELLED"),
					TRIAL: t("enums.licenses.TRIAL"),
					PAUSED: t("enums.licenses.PAUSED"),
					MANUAL: t("enums.licenses.MANUAL"),
					NONE: t("enums.licenses.NONE"),
				},
			},
			{
				id: "license_expires_at",
				name: t("modules.organization.field.license_expires_at.title"),
				type: "date",
			},
			{
				id: "first_paid_at",
				name: t("common.word.first_paid_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "gopos_last_used_at",
				name: t("modules.organization.field.gopos_last_used_at.title"),
				type: "date",
			},
			{
				id: "gopos_created_at",
				name: t("modules.organization.field.gopos_created_at.title"),
				type: "date",
			},
			{
				id: "gopos_license_expires_at",
				name: t("modules.organization.field.gopos_license_expires_at.title"),
				type: "date",
			},
			{
				id: "gopos_activated_at",
				name: t("modules.organization.field.gopos_activated_at.title"),
				type: "date",
			},
			{
				id: "company_tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "company_id",
				name: t("common.word.company_id"),
				type: "text",
			},
			{
				id: "company_street",
				name: t("modules.organization.field.company_street.title"),
				type: "text",
			},
			{
				id: "company_city",
				name: t("modules.organization.field.company_city.title"),
				type: "text",
			},
			{
				id: "company_email",
				name: t("modules.organization.field.company_email.title"),
				type: "text",
			},
			{
				id: "company_name",
				name: t("common.word.company_name"),
				type: "text",
			},
			{
				id: "company_phone_number",
				name: t("modules.organization.field.company_phone_number.title"),
				type: "text",
			},
			{
				id: "place_street",
				name: t("lib:common.address.street"),
				type: "text",
			},
			{
				id: "place_city",
				name: t("lib:common.address.city"),
				type: "text",
			},
			{
				id: "place_email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "place_name",
				name: t("modules.organization.field.place_name.title"),
				type: "text",
			},
			{
				id: "place_phone_number",
				name: t("lib:common.word.phone_number"),
				type: "text",
			},
			{
				id: "place_facebook",
				name: t("modules.organization.field.place_facebook.title"),
				type: "text",
			},
			{
				id: "place_instagram",
				name: t("modules.organization.field.place_instagram.title"),
				type: "text",
			},
			{
				id: "place_website",
				name: t("modules.organization.field.place_website.title"),
				type: "text",
			},
			{
				id: "place_linkedin",
				name: t("modules.organization.field.place_linkedin.title"),
				type: "text",
			},
			{
				id: "terminal_count",
				name: t("modules.organization.field.terminal_count.title"),
				type: "number",
			},
			{
				id: "tag",
				name: t("common.word.tags"),
				type: "search_select",
				source: {
					request: async (search: string) => {
						const f = mapToBase(search, { resource_type: "ORGANIZATION" });
						return mapResponseToResponseDataWithId(await adminApi.getTagsListSearchSelect(f));
					},
				},
			},
			{
				id: "billing_source_id",
				name: t("modules.organization.field.billing_source_id.title"),
				type: "text",
			},
			{
				id: "partner_count",
				name: t("modules.organization.field.partner_count.title"),
				type: "number",
			},
			{
				id: "partner_with_provision_count",
				name: t("modules.organization.field.partner_with_provision_count.title"),
				type: "number",
			},
			{
				id: "not_paid_invoices_amount",
				name: t("modules.organization.field.not_paid_invoices_amount.title"),
				type: "number",
			},
			{
				id: "not_paid_invoices_count",
				name: t("modules.organization.field.not_paid_invoices_count.title"),
				type: "number",
			},
			{
				id: "days_past_payment_date_due",
				name: t("modules.organization.field.days_past_payment_date_due.title"),
				type: "number",
			},
			{
				id: "gopos_closed_pos_reports_count",
				name: t("modules.organization.field.gopos_closed_pos_reports_count.title"),
				type: "number",
			},
			{
				id: "gopos_last_closed_pos_report_at",
				name: t("modules.organization.field.gopos_last_closed_pos_report_at.title"),
				type: "date",
			},
			{
				id: "mrr",
				name: t("common.word.mrr"),
				type: "number",
			},
			{
				id: "mrr_sum",
				name: t("common.word.mrr_sum"),
				type: "number",
			},
			{
				id: "arpu",
				name: t("modules.organization.field.arpu.title"),
				type: "number",
			},
			{
				id: "customer_lifetime",
				name: t("modules.organization.field.customer_lifetime.title"),
				type: "number",
			},
			{
				id: "migrated_at",
				name: t("common.word.migrated_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "type",
				name: t("common.word.type", { ns: "lib" }),
				type: "list",
				listOptions: Object.entries(OrganizationTypeApi).reduce((options, [entryKey, entryValue]) => {
					const newOptions = { ...options };
					newOptions[entryKey] = `${entryValue.substring(0, 1)}${entryValue.substring(1).toLowerCase()}`;
					return newOptions;
				}, {}),
				hasDefaultOptions: false,
			},
			{
				id: "potential_product",
				name: t("modules.organization.field.potential_product.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.getOrganizationProductSearchSelect(search, params, options),
				},
				withoutInfiniteScroll: true,
			},
			{
				id: "product",
				name: t("modules.organization.field.product.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.getOrganizationProductSearchSelect(search, params, options),
				},
				withoutInfiniteScroll: true,
			},
			{
				id: "abused_access",
				name: t("modules.organization.field.abused_access.title"),
				type: "number",
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: OrganizationApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: OrganizationApi) => `${location.pathname}/${item.id}/edit`,
			},
			{
				name: `${t("lib:common.action.goto")}`,
				link: (item: OrganizationApi) => {
					return `/${item.id}`;
				},
				visible: (item: OrganizationApi) =>
					user?.admin || user?.organizations?.map((x) => x.id).includes(item.id),
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: OrganizationApi) => onRemoveOrganization(item),
				visible: (item: OrganizationApi) => user?.admin && item.status !== "REMOVED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.block_license"),
				click: (item: OrganizationApi) => onBlockLicense(item),
				visible: (item: OrganizationApi) => item.status === "ENABLED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.unblock_license"),
				click: (item: OrganizationApi) => onUnblockLicense(item),
				visible: (item: OrganizationApi) => item.status === "BLOCKED",
				refreshListAfter: true,
			},
			{
				name: t("modules.organization.action.change_partner.title"),
				link: (item: OrganizationApi) => `${location.pathname}/${item.id}/partner`,
			},
			{
				name: t("modules.organization.action.merge_organization.title"),
				click: (item: OrganizationApi) => {
					setOrganizationToMerge(item);
					setShowOrganizationMergingModal(true);
				},
			},
		],
		selectedColumns: [
			"name",
			"category",
			"license_type",
			"created_at",
			"last_used_at",
			"application_type",
			"company_name",
			"contact_person",
			"partners",
			"potential_products",
			"products",
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "applications,partner,sub_partner,partners,custom_fields,products,stats,features";
			setParams(params);
			return adminApi.getAdminOrganizations(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.organization.header.title"),
			filename: t("modules.organization.field.export_config.filename"),
			organization: organization.name,
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
		filterValues: data.filter_values,
	};

	return (
		<>
			<div className="organizations-table">
				<ListData data={items} config={config} onFetch={(items: OrganizationApi[]) => setItems(items)} />
			</div>
			{showOrganizationMergingModal && (
				<OrganizationMergingModal
					onHide={() => {
						setShowOrganizationMergingModal(false);
						setOrganizationToMerge(undefined);
					}}
					isShown={showOrganizationMergingModal}
					organization={organizationToMerge as OrganizationApi}
					fetchOrganization={refresh}
				/>
			)}
			{showPartnersProvisionsModal && (
				<OrganizationPartnersProvisionModal
					isShown={showPartnersProvisionsModal}
					onHide={() => setShowPartnersProvisionsModal(undefined)}
					organizationId={showPartnersProvisionsModal}
					isAdminMode
				/>
			)}
		</>
	);
};

const AdminOrganizationsIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "ORGANIZATION",
			name: t(`enums.custom_fields.resources.ORGANIZATION`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.organization.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.organization.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default AdminOrganizationsIndexPage;
